import React, { useState, useEffect } from 'react'
import emailjs from '@emailjs/browser'
import { motion, AnimatePresence } from 'framer-motion'
import { MdClose, MdArrowOutward, MdOutlineMarkEmailRead } from 'react-icons/md'

/*
 * Sends a notification to YOUR inbox that someone requested your resume.
 * Reuses your existing contact-form EmailJS service + template, so no new
 * setup is required — the template's "To email" already points at your inbox.
 *
 * The template is sent these variables (your contact template already uses
 * userName / subject / message, so they map straight in):
 *   userName  -> "Resume Request"
 *   subject   -> who requested it
 *   message   -> full note, incl. the requester's email
 *   reply_to  -> the requester's email (so you can reply with the file)
 *
 * If you'd rather use a dedicated template, just create one in EmailJS and
 * swap TEMPLATE_ID below.
 */
const SERVICE_ID = process.env.REACT_APP_SERVICE_ID
const TEMPLATE_ID = process.env.REACT_APP_TEMPLATE_ID_TWO
const PUBLIC_KEY = process.env.REACT_APP_PUBLIC_KEY 

export const ResumeModal = ({ open, onClose }) => {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle') // idle | sending | success | error

  // close on Escape + lock background scroll while open
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  // reset state shortly after closing so it reopens clean
  useEffect(() => {
    if (open) return
    const t = setTimeout(() => { setStatus('idle'); setEmail('') }, 300)
    return () => clearTimeout(t)
  }, [open])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')
    const params = {
      userName: 'Resume Request',
      subject: `Resume request from ${email}`,
      message: `${email} would like a copy of your resume. Reply to this email to send it over.`,
      reply_to: email,
      email,
    }
    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, params, PUBLIC_KEY)
      setStatus('success')
    } catch (err) {
      console.error('Resume request failed:', err)
      setStatus('error')
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <div className=' fixed inset-0 z-[100] flex items-center justify-center p-4'>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className=' absolute inset-0 bg-ink/60 backdrop-blur-sm'
          />
          <motion.div
            role='dialog'
            aria-modal='true'
            aria-labelledby='resume-modal-title'
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className=' relative z-10 w-full max-w-md bg-cream border border-ink/10 rounded-2xl shadow-2xl p-7 md:p-8'>
            <button onClick={onClose} aria-label='Close' className=' absolute top-4 right-4 text-ink/40 hover:text-maroon transition-colors'>
              <MdClose className=' text-2xl' />
            </button>

            {status === 'success' ? (
              <div className=' text-center py-4'>
                <MdOutlineMarkEmailRead className=' text-5xl text-mint-deep mx-auto mb-4' />
                <h3 id='resume-modal-title' className=' font-display text-3xl text-maroon tracking-tight mb-2'>Request received!</h3>
                <p className=' text-sm text-ink/60'>
                  I&apos;ll send a copy of my resume to <span className=' text-maroon font-medium break-all'>{email}</span> shortly.
                </p>
                <button onClick={onClose} className=' mt-6 inline-flex items-center gap-2 bg-maroon text-cream text-sm font-medium px-6 py-3 rounded-full hover:bg-mint hover:text-panel transition-colors'>
                  Done
                </button>
              </div>
            ) : (
              <>
                <p className=' font-mono text-xs uppercase tracking-[0.2em] text-mint-deep mb-3'>Request my resume</p>
                <h3 id='resume-modal-title' className=' font-display text-3xl text-maroon tracking-tight mb-2'>Where should I send it?</h3>
                <p className=' text-sm text-ink/60 mb-6'>Drop your email and I&apos;ll send a copy of my resume straight to your inbox.</p>
                <form onSubmit={handleSubmit} className=' flex flex-col gap-4'>
                  <input
                    type='email'
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder='you@example.com'
                    className=' w-full bg-raise border border-ink/15 rounded-lg px-4 py-3 text-ink placeholder:text-ink/30 outline-none focus:border-mint-deep focus:ring-1 focus:ring-mint-deep transition-colors'
                  />
                  {status === 'error' && (
                    <p className=' text-xs text-red-600'>
                      Something went wrong sending your request. Please try again or reach me directly.
                    </p>
                  )}
                  <button
                    type='submit'
                    disabled={status === 'sending'}
                    className=' group inline-flex items-center justify-center gap-2 bg-maroon text-cream text-sm font-medium px-6 py-3.5 rounded-full hover:bg-mint hover:text-panel transition-colors disabled:opacity-60 disabled:pointer-events-none'>
                    {status === 'sending' ? 'Sending…' : 'Request resume'}
                    <MdArrowOutward className=' text-lg transition-transform duration-300 group-hover:rotate-45' />
                  </button>
                </form>
                <p className=' mt-4 text-[11px] text-ink/40 text-center'>No spam — your email is only used to send you the resume.</p>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
